// Holds one decision card's record and its one in-flight decision. The same
// decision keeps the same operation id until the record changes, so a second
// tap or a re-render can never become a second effect. A call that fails or
// answers unreadably leaves the outcome unknown: the card offers a status
// check and never a new decision. The status check is READ-ONLY: it reads the
// record and never sends the decision again, because a request lost before the
// producer admitted it has no effect and a resend would be its first. The UI
// adds no receipt or status authority of its own: a lost decision stays
// unresolved until the producer (PUB) maps an authorized receipt lookup, and
// until then every read leaves it locked (fail closed).
import { useCallback, useEffect, useRef, useState } from 'react';
import type { AppDecisionsApi } from './app-decisions-api';
import type { AppDecision, AppDecisionKind, DecideInput } from './app-decision-types';

export type DecisionNotice =
  | null
  | 'changed'
  | 'not_authorized'
  | 'expired'
  | 'invalid'
  | 'unavailable';

export type DecisionRequest = Pick<DecideInput, 'decision' | 'excludedRowIds' | 'stepOrder'>;

export function useAppDecision(
  api: AppDecisionsApi,
  kind: AppDecisionKind,
  id: string,
  initial: AppDecision | null,
  onSettled?: (record: AppDecision) => void,
) {
  const [record, setRecord] = useState<AppDecision | null>(initial);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  // The decision whose answer never came back; it stays unresolved until the
  // server settles that exact operation.
  const [lost, setLostState] = useState(false);
  const unresolved = useRef<DecideInput | null>(null);
  // What the last status check said, when it settled nothing.
  const [stillOpen, setStillOpen] = useState<null | 'pending' | 'unknown'>(null);
  const setLost = useCallback((value: boolean) => {
    setLostState(value);
    if (!value) {
      unresolved.current = null;
      setStillOpen(null);
    }
  }, []);
  const [notice, setNotice] = useState<DecisionNotice>(null);
  const operation = useRef<{ key: string; id: string } | null>(null);
  const flying = useRef(false);
  const alive = useRef(true);
  const settled = useRef(onSettled);
  settled.current = onSettled;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    setLoadFailed(false);
    try {
      const next = await api.get(kind, id);
      if (!alive.current) return;
      // A plain read shows the record; it never settles a lost decision.
      setRecord(next);
    } catch {
      if (alive.current) setLoadFailed(true);
    }
  }, [api, kind, id]);

  // "Check status" is READ-ONLY: it never sends the decision again (a request
  // lost before the producer admitted it has no effect, and a resend would be
  // its first). It reads the record for display, then asks PUB's authorized
  // receipt lookup about the ORIGINAL decision. Only that settles it: an
  // applied answer that echoes all six fields of the original decision exactly
  // with a receipt id and its canonical record; or a not-applied with PUB's
  // terminal reference.
  // A matching record, a recorded step, pending, absent, expired, revoked,
  // unreadable or an unregistered route all leave it locked.
  const checkStatus = useCallback(async () => {
    const original = unresolved.current;
    if (!original) return refresh();
    if (flying.current) return;
    setBusy(true);
    try {
      let shown: AppDecision | null = null;
      try {
        shown = await api.get(kind, id);
      } catch {
        shown = null;
      }
      if (!alive.current || unresolved.current !== original) return;
      if (shown) setRecord(shown);
      const answer = await api.receiptStatus({
        kind: original.kind,
        id: original.id,
        expectedVersion: original.expectedVersion,
        decision: original.decision,
        operationId: original.operationId,
        inputDigest: original.inputDigest,
      });
      if (!alive.current || unresolved.current !== original) return;
      if (
        answer.status === 'applied' &&
        answer.record.kind === original.kind &&
        answer.record.id === original.id
      ) {
        setLost(false);
        operation.current = null;
        setRecord(answer.record);
        settled.current?.(answer.record);
      } else if (answer.status === 'not_applied') {
        setLost(false);
        operation.current = null;
      } else setStillOpen(answer.status === 'pending' ? 'pending' : 'unknown');
    } catch {
      if (alive.current) setStillOpen('unknown');
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [api, kind, id, refresh, setLost]);

  useEffect(() => {
    if (!initial) void refresh();
  }, [initial, refresh]);

  const decide = useCallback(
    async (request: DecisionRequest) => {
      if (!record || flying.current || unresolved.current) return;
      flying.current = true;
      setBusy(true);
      setNotice(null);
      const excluded = [...(request.excludedRowIds ?? [])].sort();
      const key = JSON.stringify([record.version, request.decision, excluded, request.stepOrder]);
      if (operation.current?.key !== key)
        operation.current = { key, id: globalThis.crypto.randomUUID() };
      const command: Omit<DecideInput, 'inputDigest'> = {
        kind,
        id: record.id,
        expectedVersion: record.version,
        decision: request.decision,
        excludedRowIds: request.excludedRowIds ? excluded : undefined,
        stepOrder: request.stepOrder,
        operationId: operation.current.id,
      };
      // The digest comes from the trusted registered command mapping, never from
      // here. Without one nothing is sent and nothing is locked: the decision
      // simply cannot be made yet.
      let inputDigest: string;
      try {
        inputDigest = await api.commandDigest(command);
      } catch {
        operation.current = null;
        if (alive.current) {
          setNotice('unavailable');
          setBusy(false);
        }
        flying.current = false;
        return;
      }
      let sent: DecideInput | null = null;
      try {
        const input: DecideInput = { ...command, inputDigest };
        sent = input;
        const result = await api.decide(input);
        if (!alive.current) return;
        if (result.kind === 'ok') {
          setRecord(result.record);
          setLost(false);
          settled.current?.(result.record);
        } else if (result.kind === 'conflict') {
          setRecord(result.current);
          setNotice('changed');
          operation.current = null;
        } else {
          setNotice(result.reason);
          operation.current = null;
        }
      } catch {
        // The call did not come back: whether it happened is not known.
        if (alive.current && sent) {
          unresolved.current = sent;
          setLostState(true);
        }
      } finally {
        flying.current = false;
        if (alive.current) setBusy(false);
      }
    },
    [api, kind, record, setLost],
  );

  return { record, loadFailed, busy, lost, stillOpen, notice, decide, refresh, checkStatus };
}
